import React from 'react';
import { MdChevronLeft, MdChevronRight, MdFirstPage, MdLastPage } from 'react-icons/md';

import { anelDeFoco, styledDs as styled } from '../../../styles/theme';

export type DsPaginationProps = {
  pagina: number;
  tamanhoPagina: number;
  totalRegistros: number;
  opcoesTamanho?: number[];
  desabilitado?: boolean;
  onPaginaChange: (pagina: number) => void;
  onTamanhoPaginaChange: (tamanhoPagina: number) => void;
};

const OPCOES_PADRAO = [10, 20, 50, 100];

export function janelaDePaginas(pagina: number, totalPaginas: number, maximo = 5): number[] {
  if (totalPaginas <= maximo) {
    return Array.from({ length: totalPaginas }, (_, indice) => indice + 1);
  }

  const meio = Math.floor(maximo / 2);
  const inicio = Math.min(Math.max(pagina - meio, 1), totalPaginas - maximo + 1);

  return Array.from({ length: maximo }, (_, indice) => inicio + indice);
}

const Container = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.font.family};
`;

const Grupo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Rotulo = styled.span`
  font-size: ${({ theme }) => theme.font.sizeSm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Seletor = styled.select`
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 0 6px;
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeSm};
  cursor: pointer;

  &:focus-visible {
    ${anelDeFoco('-1px')}
  }
`;

const Passo = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  svg {
    width: 17px;
    height: 17px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accentSoft};
    color: ${({ theme }) => theme.colors.accent};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: default;
    opacity: 0.5;
  }

  &:focus-visible {
    ${anelDeFoco('-1px')}
  }
`;

const Pagina = styled.button<{ $ativa: boolean }>`
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $ativa }) => ($ativa ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $ativa }) => ($ativa ? theme.colors.onAccent : theme.colors.textSecondary)};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeSm};
  font-weight: ${({ theme, $ativa }) => ($ativa ? theme.weight.semibold : theme.weight.normal)};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme, $ativa }) => ($ativa ? theme.colors.accent : theme.colors.accentSoft)};
  }

  &:focus-visible {
    ${anelDeFoco('-1px')}
  }
`;

export const DsPagination: React.FC<DsPaginationProps> = ({
  pagina,
  tamanhoPagina,
  totalRegistros,
  opcoesTamanho = OPCOES_PADRAO,
  desabilitado = false,
  onPaginaChange,
  onTamanhoPaginaChange,
}) => {
  const totalPaginas = Math.max(Math.ceil(totalRegistros / tamanhoPagina), 1);
  const primeiroItem = totalRegistros === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalRegistros);
  const naPrimeira = pagina <= 1;
  const naUltima = pagina >= totalPaginas;

  const contagem =
    totalRegistros === 0
      ? 'Nenhum registro'
      : `${primeiroItem} - ${ultimoItem} de ${totalRegistros} registros`;

  return (
    <Container aria-label="Paginação">
      <Grupo>
        <Rotulo>Exibir</Rotulo>
        <Seletor
          aria-label="Registros por página"
          value={tamanhoPagina}
          disabled={desabilitado}
          onChange={({ target }) => onTamanhoPaginaChange(Number(target.value))}
        >
          {opcoesTamanho.map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </Seletor>
        <Rotulo>{contagem}</Rotulo>
      </Grupo>

      <Grupo>
        <Passo
          type="button"
          aria-label="Primeira página"
          disabled={desabilitado || naPrimeira}
          onClick={() => onPaginaChange(1)}
        >
          <MdFirstPage aria-hidden="true" />
        </Passo>
        <Passo
          type="button"
          aria-label="Página anterior"
          disabled={desabilitado || naPrimeira}
          onClick={() => onPaginaChange(pagina - 1)}
        >
          <MdChevronLeft aria-hidden="true" />
        </Passo>

        {janelaDePaginas(pagina, totalPaginas).map((numero) => (
          <Pagina
            key={numero}
            type="button"
            $ativa={numero === pagina}
            aria-label={`Página ${numero}`}
            aria-current={numero === pagina ? 'page' : undefined}
            disabled={desabilitado}
            onClick={() => onPaginaChange(numero)}
          >
            {numero}
          </Pagina>
        ))}

        <Passo
          type="button"
          aria-label="Próxima página"
          disabled={desabilitado || naUltima}
          onClick={() => onPaginaChange(pagina + 1)}
        >
          <MdChevronRight aria-hidden="true" />
        </Passo>
        <Passo
          type="button"
          aria-label="Última página"
          disabled={desabilitado || naUltima}
          onClick={() => onPaginaChange(totalPaginas)}
        >
          <MdLastPage aria-hidden="true" />
        </Passo>
      </Grupo>
    </Container>
  );
};
